import Component from '../abstracts/Component';

export default class CollapseComponent extends Component {

  get COMPONENTNAME() {
    return 'CollapseComponent';
  }

  get SELECTORS() {
    return {
      trigger: '[data-trigger]',
      tab: '[data-tab]',
      button: '[data-collapsable-button]',
    };
  }

  get CLASSES() {
    return {
      collapsed: 'collapsed'
    }
  }

  get BREAKPOINTS() {
    return {
      large: 1024,
      medium: 768
    }
  }

  constructor(compEl) {
    super(compEl);
    this.isDesktop = false;
    this.isFooterComponent = this.checkIfFooterComponent();
    this.isServiceNavComponent = this.checkIfServiceNavComponent();
    this.collapseMaxRes = this.getCollapseMaxRes();
    this.intersectionObserver = null;
    this.isScrolling = false;
    this.scrollListeners = new Set();
  }

  checkIfFooterComponent() {
    return this.$component.closest('.c-footer') !== null;
  }

  checkIfServiceNavComponent() {
    return this.$component.closest('.service-navigation') !== null;
  }

  getCollapseMaxRes() {
    const maxRes = this.$component.dataset.collapseMaxres;
    if (maxRes === 'md') return 'medium';
    if (maxRes === 'lg') return 'large';
    return null;
  }

  readDOM() {
    return {
      triggers: this.$component.querySelectorAll(this.SELECTORS.trigger),
      tabs: this.$component.querySelectorAll(this.SELECTORS.tab),
      currentOpenTab: null,
    };
  }

  init() {
    this.checkScreenSize();

    if(this.dom.tabs) {
      this.dom.tabs.forEach(tab => {
        tab.style.height = tab.offsetHeight + "px";
      });
    }

    if(this.dom.triggers) {
      this.dom.triggers.forEach(trigger => {
        if(trigger.dataset.trigger === "start-open") {
          this.dom.currentOpenTab = trigger;
          this.setTabVisibility(trigger, true);
        } else {
          // check if should collapse
          const shouldCollapse = this.shouldComponentCollapse();

          if (shouldCollapse) {
            trigger.classList.add(this.CLASSES.collapsed);
            this.setTabVisibility(trigger, false);
          } else {
            trigger.classList.remove(this.CLASSES.collapsed);
            this.setTabVisibility(trigger, true);
          }
        }

        // Disable click
        const shouldDisableClick = (this.isFooterComponent && this.isDesktop) ||
                                  (this.isServiceNavComponent && this.collapseMaxRes === 'medium' && this.isDesktop);

        if (shouldDisableClick) {
          this.disableTriggerClick(trigger);
        } else {
          this.enableTriggerClick(trigger);
        }
      });
    }
  }

  checkScreenSize() {
    this.isDesktop = window.innerWidth >= this.BREAKPOINTS.large;
    this.isMediumOrLarger = window.innerWidth >= this.BREAKPOINTS.medium;
  }

  shouldComponentCollapse() {
    if (this.isFooterComponent || (this.isServiceNavComponent && this.collapseMaxRes === 'medium')) {
      return !this.isDesktop;
    }
    return true;
  }

  disableTriggerClick(trigger) {
    trigger.style.cursor = 'default';
    trigger.style.pointerEvents = 'none';
    const links = trigger.querySelectorAll('a');
    links.forEach(link => {
      link.style.pointerEvents = 'auto';
    });
  }

  enableTriggerClick(trigger) {
    trigger.style.cursor = '';
    trigger.style.pointerEvents = '';
    const links = trigger.querySelectorAll('a');
    links.forEach(link => {
      link.style.pointerEvents = '';
    });
  }

  scrollToAccordionHeader(trigger) {
    const scrollContainer = trigger.closest('.pdp__info');
    if (!scrollContainer) {
      this.isScrolling = false;
      return;
    }
    const containerRect = scrollContainer.getBoundingClientRect();
    const targetTop = Math.max(60, (window.innerHeight - containerRect.height) / 2);
    this.performScroll(window, window.pageYOffset + containerRect.top - targetTop, () => this.scrollInsideContainer(trigger, scrollContainer));
  }

  scrollInsideContainer(trigger, scrollContainer) {
    const containerRect = scrollContainer.getBoundingClientRect();
    const headerRect = trigger.getBoundingClientRect();
    const headerRelativeTop = headerRect.top - containerRect.top;
    const isHeaderVisible = headerRelativeTop >= 20 && headerRelativeTop <= containerRect.height * 0.3;

    if (!isHeaderVisible) {
      const targetScrollTop = Math.max(0, scrollContainer.scrollTop + headerRelativeTop - 20);
      this.performScroll(scrollContainer, targetScrollTop, () => this.finishScrollOperation());
    } else {
      this.finishScrollOperation();
    }
  }

  performScroll(element, targetPos, onComplete) {
    this.cleanupScrollListeners();
    const startTime = Date.now();
    const isWindow = element === window;

    const interrupt = () => { this.cleanupScrollListeners(); this.finishScrollOperation(); };
    const complete = () => { this.cleanupScrollListeners(); onComplete(); };

    const checkPosition = () => {
      const current = isWindow ? window.pageYOffset : element.scrollTop;
      if (Math.abs(current - targetPos) <= 4 || Date.now() - startTime >= 1500) {
        complete();
      } else {
        requestAnimationFrame(checkPosition);
      }
    };

    this.addScrollListener(element, 'wheel', interrupt, { passive: true });
    this.addScrollListener(element, 'touchmove', interrupt, { passive: true });

    if ('onscrollend' in element) {
      this.addScrollListener(element, 'scrollend', complete);
    } else {
      requestAnimationFrame(checkPosition);
    }

    element.scrollTo({ top: targetPos, behavior: 'smooth' });
  }


  addScrollListener(element, event, handler, options = {}) {
    const listenerInfo = { element, event, handler, options };
    this.scrollListeners.add(listenerInfo);
    element.addEventListener(event, handler, options);
  }

  cleanupScrollListeners() {
    this.scrollListeners.forEach(({ element, event, handler, options }) => {
      element.removeEventListener(event, handler, options);
    });
    this.scrollListeners.clear();
  }

  finishScrollOperation() {
    this.cleanupScrollListeners();
    this.isScrolling = false;
  }

  setupAutoCloseObserver() {
    const isPdpDetails = this.$component.classList.contains('pdp__details');
    if (!isPdpDetails) return;

    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
    }

    this.intersectionObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (!entry.isIntersecting && this.dom.currentOpenTab && !this.isScrolling) {
          this.closeCurrentAccordion();
        }
      });
    }, {
      threshold: 0.1,
      rootMargin: '-50px 0px -50px 0px'
    });

    this.intersectionObserver.observe(this.$component);
  }

  closeCurrentAccordion() {
    if (this.dom.currentOpenTab) {
      this.dom.currentOpenTab.classList.add(this.CLASSES.collapsed);
      this.dom.currentOpenTab.querySelector(this.SELECTORS.button)?.setAttribute("aria-expanded", "false");
      this.setTabVisibility(this.dom.currentOpenTab, false);
      this.dom.currentOpenTab = null;
    }
  }

  handleCollapse() {
    if(this.dom.triggers) {
      this.dom.triggers.forEach(trigger => {
        trigger.addEventListener("click", () => {
          if(this.dom.currentOpenTab === trigger) {
            trigger.classList.add(this.CLASSES.collapsed);
            trigger.querySelector(this.SELECTORS.button)?.setAttribute("aria-expanded","false");
            this.setTabVisibility(trigger, false);
            this.dom.currentOpenTab = null;
          } else {
            if(this.dom.currentOpenTab != null) {
              this.dom.currentOpenTab.classList.add(this.CLASSES.collapsed);
              this.dom.currentOpenTab.querySelector(this.SELECTORS.button)?.setAttribute("aria-expanded","false");
              this.setTabVisibility(this.dom.currentOpenTab, false);
            }

            const shouldScroll = this.shouldScrollToAccordion(trigger);

            trigger.classList.remove(this.CLASSES.collapsed);
            trigger.querySelector(this.SELECTORS.button)?.setAttribute("aria-expanded","true");
            this.setTabVisibility(trigger, true);
            this.dom.currentOpenTab = trigger;

            if (shouldScroll) {
              this.isScrolling = true;
              this.scrollToAccordionHeader(trigger);
            }
          }
        });
      });
    }
  }

  shouldScrollToAccordion(trigger) {
    const isPdpDetails = trigger.closest('.pdp__details') !== null;
    if (!isPdpDetails) return false;

    const scrollContainer = trigger.closest('.pdp__info');
    if (!scrollContainer) return false;

    const containerRect = scrollContainer.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const headerOffset = 60;
    const bottomMargin = 100;

    const containerTop = containerRect.top;
    const containerBottom = containerRect.bottom;
    const availableViewport = viewportHeight - headerOffset - bottomMargin;

    return containerTop < headerOffset ||
           containerBottom > viewportHeight - bottomMargin ||
           containerRect.height > availableViewport * 0.8;
  }

  setTabVisibility(trigger, isVisible) {
    const tabContent = trigger.parentNode.querySelector(this.SELECTORS.tab);
    if (tabContent) {
      const isSplashCountrySelector = tabContent.closest('.s-p__continent');
      if (!isSplashCountrySelector) {
        tabContent.style.visibility = isVisible ? 'visible' : 'hidden';
      }
    }
  }

  bindEvents() {
    this.handleCollapse();
    window.addEventListener('resize', () => {
      const wasDesktop = this.isDesktop;
      this.checkScreenSize();
      const shouldReinit = (wasDesktop !== this.isDesktop && this.isFooterComponent) ||
                          (wasDesktop !== this.isDesktop && this.isServiceNavComponent);

      if (shouldReinit) {
        this.init();
      }

      if (this.dom.currentOpenTab) {
        const tab = this.dom.currentOpenTab.parentNode.querySelector(this.SELECTORS.tab);
        tab.style.maxHeight = tab.scrollHeight + "px";
      }
    });
  }

  destroy() {
    this.cleanupScrollListeners();
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
      this.intersectionObserver = null;
    }
    this.isScrolling = false;
  }

  render() {
    super.render();
    this.log('Rendering...');
    this.dom = this.readDOM();
    this.init();
    this.bindEvents();
    this.setupAutoCloseObserver();
  }
}
